import { useState } from 'react';

import { SMTFoodEntryData } from '@equip.health/ui';

import AnalyticsEvents from '~/analytics';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import {
  useReplaceSMTEntryImageMutation,
  useUpdateSMTEntryMutation,
  useUploadSMTEntryImageMutation,
} from '~/store/services/SMT';

import {
  transformFoodEntryDataToUpdateRequest,
  transformSMTEntryToFoodEntryData,
} from '~/components/maud/patients/profile/smt/foodEntryUtils';
import { uploadSMTImage } from '~/components/maud/patients/profile/smt/hooks/uploadSMTImage.util';

interface UseEditSMTEntryParams {
  patientId: string;
}

interface UseEditSMTEntryReturn {
  entryDataToEdit?: SMTFoodEntryData;
  entryIdToEdit?: string;
  handleCloseEditModal: () => void;
  handleConfirmEdit: (data: SMTFoodEntryData) => Promise<void>;
  handleEditEntry: (entryId: string, entry?: SMTEntry) => void;
  isEditModalOpen: boolean;
  isLoading: boolean;
}

export const useEditSMTEntry = ({
  patientId,
}: UseEditSMTEntryParams): UseEditSMTEntryReturn => {
  const [entryIdToEdit, setEntryIdToEdit] = useState<string>();
  const [entryDataToEdit, setEntryDataToEdit] = useState<SMTFoodEntryData>();
  const [existingEntry, setExistingEntry] = useState<SMTEntry | null>();
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useAnalytics();
  const { logError } = useDatadog();
  const [updateSMTEntry] = useUpdateSMTEntryMutation();
  const [uploadSMTEntryImage] = useUploadSMTEntryImageMutation();
  const [replaceSMTEntryImage] = useReplaceSMTEntryImageMutation();

  const handleEditEntry = (entryId: string, entry?: SMTEntry) => {
    setEntryIdToEdit(entryId);
    setExistingEntry(entry ?? undefined);

    const entryData: SMTFoodEntryData | undefined = entry
      ? transformSMTEntryToFoodEntryData(entry)
      : undefined;

    setEntryDataToEdit(entryData);
    trackEvent(AnalyticsEvents.editEntryButtonClicked, {
      entryId,
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });
  };

  const handleConfirmEdit = async (data: SMTFoodEntryData) => {
    if (!entryIdToEdit) return;

    trackEvent(AnalyticsEvents.editEntryConfirmed, {
      entryId: entryIdToEdit,
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });

    setIsLoading(true);
    try {
      await updateSMTEntry(
        transformFoodEntryDataToUpdateRequest(data, entryIdToEdit, patientId),
      ).unwrap();

      try {
        await uploadSMTImage({
          data,
          entryId: entryIdToEdit,
          errorContext: {
            action: 'editSMTEntry',
            context: 'SMT image compression failed',
            entryId: entryIdToEdit,
            patientId,
          },
          existingEntry: existingEntry || undefined,
          logError,
          patientId,
          replaceSMTEntryImage,
          source: AnalyticsSourceValues.SMT_TAB,
          trackEvent,
          uploadSMTEntryImage,
        });
      } catch (imageError) {
        logError(imageError, {
          action: 'editSMTEntry',
          context: 'SMT entry image upload/replace failed after entry update',
          entryId: entryIdToEdit,
          patientId,
        });
      }

      setEntryIdToEdit(undefined);
      setEntryDataToEdit(undefined);
      setExistingEntry(undefined);
    } catch (error) {
      logError(error, {
        action: 'editSMTEntry',
        context: 'Failed to update SMT entry',
        entryId: entryIdToEdit,
        patientId,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    if (entryIdToEdit) {
      trackEvent(AnalyticsEvents.editEntryCanceled, {
        entryId: entryIdToEdit,
        patientId,
        source: AnalyticsSourceValues.SMT_TAB,
      });
    }
    setEntryIdToEdit(undefined);
    setEntryDataToEdit(undefined);
    setExistingEntry(undefined);
  };

  return {
    entryDataToEdit,
    entryIdToEdit,
    handleCloseEditModal,
    handleConfirmEdit,
    handleEditEntry,
    isEditModalOpen: entryIdToEdit !== undefined,
    isLoading,
  };
};
